import { useState } from 'react';
import { api } from '../../api/client.js';
import { useApiQuery } from '../../api/useApiQuery.js';
import { ImageUploadField } from '../../components/forms/ImageUploadField.jsx';
import { ConfirmDialog } from '../../components/ConfirmDialog.jsx';

export function MediaLibraryPage() {
  const [page, setPage] = useState(1);
  const pageSize = 40;
  const { data, loading, error, reload } = useApiQuery(() => api.get(`/api/media?page=${page}&pageSize=${pageSize}`), [page]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const mediaItems = data?.items ?? [];
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / pageSize));

  async function handleDelete() {
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.delete(`/api/media/${deleteTarget.id}`);
      setDeleteTarget(null);
      if (mediaItems.length === 1 && page > 1) setPage((current) => current - 1);
      else reload();
    } catch (err) {
      setDeleteError(err.message);
    } finally {
      setDeleting(false);
    }
  }

  function mediaLabel(kind) {
    if (kind === 'MENU_ITEM_IMAGE') return 'Dish photo';
    if (kind === 'BANNER') return 'Cover image';
    if (kind === 'LOGO') return 'Logo';
    if (kind === 'OTHER') return 'Image';
    return kind
      .toLowerCase()
      .replaceAll('_', ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  }

  return (
    <div className="page media-page">
      <div className="page-header">
        <div>
          <h1>Media library</h1>
          <p>Keep the photos used across your menus in one easy-to-find place.</p>
        </div>
      </div>

      <div className="media-upload panel">
        <div className="media-upload__intro">
          <h2>Upload an image</h2>
          <p>Photos are automatically optimized for fast loading. You can use them for dishes, branding, or cover images.</p>
        </div>
        <ImageUploadField
          label="Choose an image"
          value={null}
          onChange={() => reload()}
          kind="OTHER"
          hint="Best results: square or 4:3 for dish photos, 3:1 or 16:9 for cover images. JPG, PNG, WebP, GIF, AVIF, and TIFF are accepted."
        />
      </div>

      {error && <p className="field-error">{error.message}</p>}

      {loading && !data ? (
        <span className="spinner" aria-label="Loading" />
      ) : mediaItems.length === 0 ? (
        <div className="empty">
          <strong>No images yet</strong>
          <p>Photos you upload for menu items or branding will show up here.</p>
        </div>
      ) : (
        <div className="media-grid" aria-busy={loading || undefined}>
          {mediaItems.map((media, index) => (
            <figure key={media.id} className="media-card">
              <img
                src={media.url}
                alt={`${mediaLabel(media.kind)} ${index + 1}`}
                onError={(event) => {
                  event.currentTarget.hidden = true;
                }}
              />
              <figcaption>
                <div className="media-card__meta">
                  <strong>{mediaLabel(media.kind)}</strong>
                  {media.width && media.height && <span>{media.width} × {media.height}</span>}
                  {media.createdAt && <span>Added {new Date(media.createdAt).toLocaleDateString()}</span>}
                </div>
                <button type="button" className="btn btn-sm btn-danger" onClick={() => { setDeleteError(null); setDeleteTarget(media); }} aria-label={`Remove ${mediaLabel(media.kind)} ${index + 1}`}>
                  Remove
                </button>
              </figcaption>
            </figure>
          ))}
        </div>
      )}

      {data && totalPages > 1 && (
        <nav className="tenant-pagination" aria-label="Media pages">
          <button type="button" className="btn btn-sm" disabled={page <= 1 || loading} onClick={() => setPage((current) => current - 1)}>Previous</button>
          <span className="field-hint">{loading ? 'Loading…' : `Page ${data.page} of ${totalPages}`}</span>
          <button type="button" className="btn btn-sm" disabled={page >= totalPages || loading} onClick={() => setPage((current) => current + 1)}>Next</button>
        </nav>
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Remove this image?"
          description="It will no longer appear in your media library. Menu items already using it keep working until you change them."
          confirmLabel="Remove"
          busy={deleting}
          error={deleteError}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
